import type { ChatFocus } from '@/features/global-chat/use-current-focus'
import { cn } from '@/lib'
import { useCampaignsShow, usePersonShow } from '@jobilla/nexus-sdk'
import { useMatch } from '@tanstack/react-router'
import { Megaphone, User, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

/**
 * Attachment-style pill in the chat composer showing the page entity that will
 * be sent as Jobibot's focus with the next message. Removing it sends the next
 * message without page context, which clears the conversation's focus.
 */
export function FocusAttachment({ focus, onRemove }: {
	focus: ChatFocus
	onRemove: () => void
}) {
	const { t } = useTranslation('common')

	// GlobalChat floats at the root route, so the company route's context isn't
	// reachable positionally — read it off the router's matched-route state.
	const companyMatch = useMatch({ from: '/_auth/company/$companyId', shouldThrow: false })
	const jobillaOrgId = (companyMatch?.context as { jobillaOrgId?: string } | undefined)?.jobillaOrgId

	const isCampaign = focus.entity_type === 'campaign'

	// Both queries are already warm on the pages that produce a focus
	// (campaign pages / talent-pool peek), so these are cache hits.
	const campaignQuery = useCampaignsShow(isCampaign ? focus.entity_id : '', {
		enabled: isCampaign,
		staleTime: 60_000,
	})
	const personQuery = usePersonShow(jobillaOrgId ?? '', !isCampaign ? focus.entity_id : '', {
		enabled: !isCampaign && !!jobillaOrgId,
		staleTime: 60_000,
	})

	const label = isCampaign
		? campaignQuery.data?.title ?? t('jobibot.focusCampaignFallback')
		: personQuery.data?.name ?? t('jobibot.focusPersonFallback')

	return (
		<div
			className={cn(
				'flex items-center gap-1 max-w-full w-fit rounded-md border px-1.5 py-0.5 mb-1.5 text-[11px] font-medium',
				isCampaign
					? 'bg-indigo-50 border-indigo-200 text-indigo-700'
					: 'bg-amber-50 border-amber-200 text-amber-700',
			)}
			title={t('jobibot.focusAttachmentTooltip')}
		>
			{isCampaign
				? <Megaphone className="h-3 w-3 shrink-0" />
				: <User className="h-3 w-3 shrink-0" />}
			<span className="truncate">{label}</span>
			<button
				type="button"
				onClick={onRemove}
				aria-label={t('jobibot.focusAttachmentRemove')}
				title={t('jobibot.focusAttachmentRemove')}
				className="shrink-0 rounded-sm p-0.5 hover:bg-black/10"
			>
				<X className="h-3 w-3" />
			</button>
		</div>
	)
}
